import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { AddEditCostSplitForm } from 'components/templates/settlement/AddEditCostSplitModal/AddEditCostSplitForm';
import {
    ChildId,
    ChildSettlementId,
    CostAllocationFormFields,
    ManualBillCostCategory,
} from 'types';
import { settlementTexts } from 'consts/text';
import useForm from 'antd/es/form/hooks/useForm';
import { parseCostAllocationFormFieldsToAddCostAllocationDTO } from 'utils/parsers/costAllocation/parseCostAllocationFormFieldsToAddCostAllocationDTO';
import { AppModal } from '../../../../molecules';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    childId?: ChildId;
    monthlySettlementId?: ChildSettlementId;
    additionalCostId?: string;
    correctionId?: string;
    type: 'monthly-settlement' | 'additional-costs';
};

export const AddCostSplitModal = ({
    isOpen,
    onClose,
    childId,
    monthlySettlementId,
    additionalCostId,
    correctionId,
    type,
}: Props) => {
    const [form] = useForm<CostAllocationFormFields>();
    const { displaySuccess } = useSimpleNotification();

    const {
        mutate: addCostAllocation,
        error: addCostAllocationError,
        isLoading: isAddCostAllocationLoading,
    } = useAppMutation(StaffHTTPService.settlements.addCostAllocation, {
        key: ['ADD_COST_ALLOCATION'],
        onSuccess: () => {
            displaySuccess(settlementTexts.children.costAllocation.addSuccess);
            onClose();
        },
        invalidateQueryKeys: [
            ['ADDITIONAL_COST_LIST'],
            ['CHILD_SETTLEMENT', childId],
            ['CHILD_SETTLEMENT_COST_SPLIT', childId],
        ],
    });

    const onAddCostAllocationFinish = (costAllocationData: CostAllocationFormFields) => {
        const getSourceType = () => {
            if (correctionId) {
                return 'CORRECTION';
            }

            if (type === 'monthly-settlement') {
                return 'MONTHLY_SETTLEMENT';
            }

            if (type === 'additional-costs') {
                return 'ADDITIONAL_COST';
            }

            return 'NO_SOURCE';
        };

        addCostAllocation({
            costAllocation: parseCostAllocationFormFieldsToAddCostAllocationDTO({
                ...costAllocationData,
                monthlySettlementId: monthlySettlementId ?? null,
                additionalCostId,
                correctionId: correctionId ?? null,
                costCategory: costAllocationData.refersTo as ManualBillCostCategory,
                sourceType: getSourceType(),
            }),
        });
    };

    return (
        <AppModal
            width={700}
            open={isOpen}
            onCancel={onClose}
            isLoading={isAddCostAllocationLoading}
        >
            <AddEditCostSplitForm
                mode="add"
                type={type}
                form={form}
                axiosError={addCostAllocationError}
                isLoading={isAddCostAllocationLoading}
                onFinish={onAddCostAllocationFinish}
            />
        </AppModal>
    );
};
